// GLSL for Volcanoes.tsx (cone, lava pool, night glow). Plume and embers live
// in volcanoParticleShaders.ts. Same conventions as waterfallShaders.ts:
// ShaderMaterials, all motion a function of the shared `uTime`. Per-instance
// data rides in `instanceColor` (a plain float3 attribute, not a colour):
//   rock / lava: r = state level (0 dormant, 1 active, 2 erupting), g = seed
// The glow wash is a single non-instanced mesh with its own attributes
// (see volcanoGlow.ts).

export const VOLCANO_HASH = /* glsl */ `
  float hash11(float n) { return fract(sin(n * 91.3458) * 47453.5453); }
`;

/**
 * Brief crater flash pulse for erupting volcanoes (every ~4.3 s). `uMotion`
 * is 0 when time is frozen (reduced motion / particles off) so the frozen
 * frame never sits on a flash.
 */
export const VOLCANO_FLASH = /* glsl */ `
  uniform float uMotion;
  float volcFlash(float t) {
    float ph = fract(t * 0.23);
    return smoothstep(0.0, 0.03, ph) * exp(-ph * 7.0) * uMotion;
  }
`;

/** Crater cone: open frustum (y 0..1, narrow at top). */
export const ROCK_VERT = /* glsl */ `
  varying float vH;
  varying float vLvl;
  varying float vSeed;
  varying float vShade;
  varying float vAng;
  void main() {
    vH = position.y;
    vLvl = instanceColor.r;
    vSeed = instanceColor.g;
    vAng = atan(position.z, position.x);
    vec3 n = normalize(mat3(modelViewMatrix) * mat3(instanceMatrix) * normal);
    vShade = 0.45 + 0.55 * clamp(dot(n, normalize(vec3(0.35, 0.8, 0.5))), 0.0, 1.0);
    gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
  }
`;

export const ROCK_FRAG = /* glsl */ `
  uniform float uTime;
  ${VOLCANO_FLASH}
  varying float vH;
  varying float vLvl;
  varying float vSeed;
  varying float vShade;
  varying float vAng;
  void main() {
    float act = min(vLvl, 1.0);
    float er = step(1.5, vLvl);
    float grain = 0.5 + 0.5 * sin(vAng * 17.0 + vH * 11.0) * sin(vAng * 5.0 - vH * 7.0);
    vec3 rock = mix(vec3(0.09, 0.08, 0.08), vec3(0.2, 0.17, 0.15), grain * (1.0 - vH * 0.5));
    // Hot rim glow near the top, only when active; dormant gets a faint ember edge.
    float flash = er * volcFlash(uTime);
    float pulse = 0.8 + 0.2 * sin(uTime * 2.1 + vAng * 3.0);
    float rim = smoothstep(0.7, 1.0, vH);
    vec3 glow = vec3(1.0, 0.38, 0.06) * rim * (act * 0.9 + 0.08) * pulse * (1.0 + er * 0.6 + flash * 1.6);
    // Lava streaks running down the flank (active only).
    float streak = smoothstep(0.78, 1.0, sin(vAng * 6.0 + sin(vAng * 3.0) * 2.0))
                 * smoothstep(0.15, 0.95, vH) * act;
    glow += vec3(1.0, 0.3, 0.04) * streak * 0.55 * (0.75 + 0.25 * sin(uTime * 1.3 + vH * 9.0));
    // Erupting: one bright lava flow spilling down a single flank (angle from the seed).
    float da = abs(mod(vAng - vSeed * 6.2832 + 3.14159, 6.28318) - 3.14159);
    float w = mix(0.07, 0.3, 1.0 - vH);
    float flow = (1.0 - smoothstep(w * 0.45, w, da)) * smoothstep(0.0, 0.22, vH) * er;
    vec3 flowCol = mix(vec3(1.0, 0.3, 0.04), vec3(1.0, 0.8, 0.3), 1.0 - smoothstep(0.0, w * 0.5, da));
    glow += flowCol * flow * (1.1 + 0.3 * sin(vH * 16.0 - uTime * 1.6));
    gl_FragColor = vec4(rock * vShade + glow, 1.0);
  }
`;

/** Lava pool: unit circle in XZ. */
export const LAVA_VERT = /* glsl */ `
  varying vec2 vUv;
  varying float vLvl;
  void main() {
    vUv = uv;
    vLvl = instanceColor.r;
    gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
  }
`;

export const LAVA_FRAG = /* glsl */ `
  uniform float uTime;
  ${VOLCANO_FLASH}
  varying vec2 vUv;
  varying float vLvl;
  void main() {
    float act = min(vLvl, 1.0);
    float er = step(1.5, vLvl);
    vec2 p = (vUv - 0.5) * 2.0;
    float r = length(p);
    // Slow swirling flow: domain-warped sine field.
    vec2 w = p * 3.2;
    w += vec2(sin(w.y * 1.7 + uTime * 0.55), cos(w.x * 1.9 - uTime * 0.45)) * 0.7;
    float f = 0.5 + 0.5 * sin(w.x * 2.1 + w.y * 1.3 + uTime * 0.8);
    float g = 0.5 + 0.5 * sin(w.y * 2.7 - w.x * 1.1 - uTime * 0.6);
    float cracks = smoothstep(0.55, 0.95, f * g + 0.25 * (1.0 - r) + 0.2 * er);
    vec3 crust = vec3(0.07, 0.03, 0.025);
    vec3 hot = mix(vec3(1.0, 0.32, 0.04), vec3(1.0, 0.82, 0.3), f * g);
    vec3 live = mix(crust, hot, clamp(cracks * 1.25 + (1.0 - r) * 0.35, 0.0, 1.0));
    // Dormant: cooled crust with only faint, slowly breathing red fissures.
    vec3 dead = mix(crust, vec3(0.55, 0.1, 0.03), cracks * (0.22 + 0.1 * sin(uTime * 0.7)));
    vec3 col = mix(dead, live, act);
    col *= smoothstep(1.0, 0.82, r) * 0.6 + 0.4; // darker at the rim wall
    // Erupting: brighter pool and a brief white-hot crater flash.
    col += vec3(1.0, 0.62, 0.22) * er * (0.25 + 0.9 * volcFlash(uTime)) * (1.0 - r * 0.6);
    gl_FragColor = vec4(col, 1.0);
  }
`;

/**
 * Night glow wash on the neighbour tile tops. One non-instanced mesh whose
 * vertices follow each neighbour's real surface height (volcanoGlow.ts), so
 * hills / mountain relief never poke through; additive, emissive only.
 *   aRel   = vertex xz minus the vent xz (linear, so length() is exact)
 *   aState = state level of the vent (1 active, 2 erupting)
 */
export const GLOW_VERT = /* glsl */ `
  attribute vec2 aRel;
  attribute float aState;
  varying vec2 vRel;
  varying float vState;
  void main() {
    vRel = aRel;
    vState = aState;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const GLOW_FRAG = /* glsl */ `
  uniform float uTime;
  uniform float uNight;
  ${VOLCANO_FLASH}
  varying vec2 vRel;
  varying float vState;
  void main() {
    float er = step(1.5, vState);
    float d = length(vRel);
    float pulse = 0.85 + 0.15 * sin(uTime * 1.7 + d * 2.0);
    // Smooth falloff that reaches exactly zero before the far side of the ring.
    float fall = exp(-d * 0.9) * (1.0 - smoothstep(1.5, 2.7, d));
    float a = fall * uNight * pulse * (1.0 + er * (0.45 + 1.6 * volcFlash(uTime)));
    vec3 col = mix(vec3(1.0, 0.36, 0.07), vec3(1.0, 0.5, 0.12), er);
    gl_FragColor = vec4(col, a);
  }
`;
